import React, { useEffect, useState } from 'react'
import orderBy from 'lodash/orderBy'
import sortBy from 'lodash/sortBy'
import capitalize from 'lodash/capitalize'
import { CSVLink } from 'react-csv'
import format from 'date-fns/format'
import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import TablePagination from '@material-ui/core/TablePagination'
import CircularProgress from '@material-ui/core/CircularProgress'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import Launch from '@material-ui/icons/Launch'
import Input from '@material-ui/core/Input'
import SearchIcon from '@material-ui/icons/Search'
import InputAdornment from '@material-ui/core/InputAdornment'
import FilterInput from '../../../../components/FilterInput'
import { numberWithCommas } from '../../../../utils'
import { useStyles } from './styles'
import { ALL_USERS_FILTER, NO_USERS_FILTER } from '../../../../constants/common'
import { MENU_PROPS } from '../../../../constants/theme'

const PROGRESS = {
  onTrack: 'On Track',
  behind: 'Behind',
  ahead: 'Ahead'
}

const headCells = [
  { id: 'name', disablePadding: true, label: 'Name' },
  { id: 'wave', disablePadding: false, label: 'Wave' },
  { id: 'userOnboardDate', disablePadding: false, label: 'Onboard' },
  { id: 'userFirstLoginDate', disablePadding: false, label: 'Start' },
  { id: 'step', disablePadding: false, label: 'Step' },
  { id: 'daysPerStep', disablePadding: false, label: 'D/S' },
  { id: 'storyCount', disablePadding: false, label: 'Story Count' },
  { id: 'pace', disablePadding: false, label: 'Pace' },
  { id: 'email', disablePadding: false, label: 'Email' },
  { id: 'status', disablePadding: false, label: 'Status' }
]

const EnhancedTableHead = ({ classes, order, orderByField, onRequestSort }) => {
  const createSortHandler = property => event => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow className={classes.headerRow}>
        {headCells.map(headCell => (
          <TableCell
            style={{ minWidth: headCell.minWidth || undefined }}
            key={headCell.id}
            align='left'
            padding='none'
            sortDirection={orderByField === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderByField === headCell.id}
              direction={orderByField === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const UsersGrid = ({ users, handleWaveFilterChange, waveFilterValue, waves, searchInputHandler, filterText, usersLoading }) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [order, setOrder] = useState('asc')
  const [orderByField, setOrderBy] = useState('name')

  useEffect(() => {
    if (filterText.length >= 3 && users.length) {
      setPage(0)
    }
  }, [filterText, users.length])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderByField === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleWaveSelected = item => {
    setRowsPerPage(25)
    setPage(0)
    handleWaveFilterChange(item)
  }

  const getComparator = user => {
    switch (orderByField) {
      case 'name':
        return user.firstName ? user.firstName.toLowerCase() : ''
      case 'wave':
        return user.wave ? user.wave.name.toLowerCase() : ''
      case 'userOnboardDate':
        return user.activity.created ? user.activity.created : ''
      case 'userFirstLoginDate':
        return user.activity.firstLogIn ? user.activity.firstLogIn : ''
      case 'step':
        return user.progress ? user.progress.currentStep : ''
      case 'daysPerStep':
        return user.progress ? user.progress.daysPerStep : ''
      case 'storyCount':
        return user.storyCount ? user.storyCount : ''
      case 'pace':
        return user.progress ? user.progress.paceProgress.toLowerCase() : ''
      case 'email':
        return user.email ? user.email.toLowerCase() : ''
      case 'status':
        return user.status ? user.status.toLowerCase() : ''
      default:
        return user.firstName ? user.firstName.toLowerCase() : ''
    }
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, users.length - page * rowsPerPage)
  const data = orderBy(users, getComparator, order)
  const exportData = data.map(user => ({
    Name: `${user.firstName} ${user.lastName}`,
    Wave: user.wave ? user.wave.name : '',
    Onboard: user.activity.created ? format(user.activity.created, 'MM/dd/yy') : '',
    Start: user.activity.firstLogIn ? format(user.activity.firstLogIn, 'MM/dd/yy') : '',
    Step: user.progress ? user.progress.currentStep : '',
    'D/S': user.progress ? numberWithCommas(Math.round((user.progress.daysPerStep + Number.EPSILON) * 10) / 10) : '',
    'Story Count': user.storyCount ? user.storyCount : '',
    Pace: user.progress ? PROGRESS[user.progress.paceProgress] : '',
    Email: user.email,
    Status: user.status
  }))

  return (
    <div className={classes.container}>
      <Grid container item xs={12} className={classes.filters}>
        <Select
          MenuProps={MENU_PROPS}
          value={waveFilterValue.id}
          onChange={null}
          input={<FilterInput />}
          className={waveFilterValue.id === ALL_USERS_FILTER.id ? classes.dropdownSelect : classes.nonDefault}
        >
          {[ALL_USERS_FILTER, ...sortBy(waves, 'name'), NO_USERS_FILTER].map(item => {
            return (
              <MenuItem key={item.id} onClick={() => handleWaveSelected(item)} value={item.id} className={classes.menuItem}>
                {item.name}
              </MenuItem>
            )
          })}
        </Select>
        <div className={classes.rightFilters}>
          <div className={classes.inputContainer}>
            <Input
              disableUnderline={true}
              value={filterText}
              placeholder='Search'
              onChange={searchInputHandler}
              endAdornment={
                <InputAdornment position='end'>
                  <SearchIcon />
                </InputAdornment>
              }
              fullWidth
            />
          </div>
          <CSVLink
            data={exportData}
            filename={`users-export-${format(new Date(), 'MM-dd-yyyy-hh-mm')}.csv`}
            target='_blank'
            style={{ textDecoration: 'none', color: 'black' }}
          >
            <Button variant='contained' color='default' className={classes.button} startIcon={<Launch />}>
              Export
            </Button>
          </CSVLink>
        </div>
      </Grid>
      <Grid container item xs={12} className={classes.table}>
        <TableContainer>
          <Table className={classes.table} size='small'>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderByField={orderByField}
              onRequestSort={handleRequestSort}
              rowCount={users.length}
            />
            {usersLoading ? (
              <tbody className={classes.loader}>
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress color='inherit' />
                  </td>
                </tr>
              </tbody>
            ) : (
              <TableBody>
                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => (
                  <TableRow className={classes.tableRow} hover tabIndex={-1} key={`${user.id}${index}`}>
                    <TableCell align='left' padding='none'>
                      {`${user.firstName} ${user.lastName}`}
                    </TableCell>
                    <TableCell align='left'>{user.wave ? user.wave.name : ''}</TableCell>
                    <TableCell align='left'>{user.activity.created ? format(user.activity.created, 'MM/dd/yy') : ''}</TableCell>
                    <TableCell align='left'>{user.activity.firstLogIn ? format(user.activity.firstLogIn, 'MM/dd/yy') : ''}</TableCell>
                    <TableCell align='left'>{user.progress ? user.progress.currentStep : ''}</TableCell>
                    <TableCell align='left'>
                      {user.progress ? numberWithCommas(Math.round((user.progress.daysPerStep + Number.EPSILON) * 10) / 10) : ''}
                    </TableCell>
                    <TableCell align='left'>{user.storyCount ? user.storyCount : ''}</TableCell>
                    <TableCell
                      align='left'
                      style={
                        user.progress ? { color: user.progress.paceProgress === 'behind' ? '#FFD000' : '#05C34B', fontWeight: '500' } : {}
                      }
                    >
                      {user.progress ? PROGRESS[user.progress.paceProgress] || '' : ''}
                    </TableCell>
                    <TableCell align='left'>{user.email}</TableCell>
                    <TableCell
                      align='left'
                      style={user.status ? { color: user.status === 'nonstarter' ? '#ff0000' : '#05C34B', fontWeight: '500' } : {}}
                    >
                      {capitalize(user.status)}
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 50 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Grid>
    </div>
  )
}

export default UsersGrid
